import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const COLORS = [
  '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#ea580c',
  '#0891b2', '#65a30d', '#db2777', '#d97706', '#4f46e5',
  '#0d9488', '#9333ea', '#b45309', '#0284c7', '#15803d'
];

function ExpenseBreakdown({ transactions }) {
  const [groupBy, setGroupBy] = useState('category');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('expense');

  const fmt = (v) => `$${Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const filtered = useMemo(() => {
    return transactions.filter(t => {
      const date = new Date(t.date);
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      const matchesType = typeFilter === 'all' || t.type === typeFilter;
      const matchesFrom = !from || date >= from;
      const matchesTo = !to || date <= to;
      return matchesType && matchesFrom && matchesTo;
    });
  }, [transactions, dateFrom, dateTo, typeFilter]);

  const chartData = useMemo(() => {
    const grouped = {};
    filtered.forEach(t => {
      let key;
      if (groupBy === 'category') key = t.category || 'Uncategorised';
      else if (groupBy === 'subCategory') key = t.subCategory || 'Uncategorised';
      else if (groupBy === 'description') key = t.description || 'Unknown';
      else if (groupBy === 'month') {
        const d = new Date(t.date);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += Math.abs(Number(t.amount));
    });

    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 20); // cap at 20 bars
  }, [filtered, groupBy]);

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  if (transactions.length === 0) return null;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '24px',
      marginBottom: '28px'
    }}>
      {/* Title */}
      <div style={{
        fontFamily: 'DM Mono, monospace',
        fontSize: '11px',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: 'var(--muted)',
        marginBottom: '20px'
      }}>
        Expense Breakdown
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.5px' }}>Group By</label>
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'var(--text)', outline: 'none' }}
          >
            <option value="category">Category</option>
            <option value="subCategory">Sub Category</option>
            <option value="description">Description</option>
            <option value="month">Month</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.5px' }}>Type</label>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'var(--text)', outline: 'none' }}
          >
            <option value="expense">Expenses</option>
            <option value="income">Income</option>
            <option value="transfer">Transfers</option>
            <option value="all">All</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.5px' }}>From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'var(--text)', outline: 'none' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.5px' }}>To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'var(--text)', outline: 'none' }}
          />
        </div>

        {(dateFrom || dateTo) && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'transparent', fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', cursor: 'pointer' }}
            >
              clear
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', marginBottom: '16px' }}>
        {chartData.length} groups · total {fmt(total)}
      </div>

      {chartData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'DM Mono, monospace', fontSize: '12px', color: 'var(--muted)' }}>
          no data for selected filters
        </div>
      ) : (
        <>
          {/* Bar Chart */}
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e6e1" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontFamily: 'DM Mono, monospace', fontSize: 10, fill: '#9b9690' }}
                axisLine={false}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmt}
              />
              <Tooltip
                formatter={(value) => [fmt(value), 'Amount']}
                contentStyle={{
                  fontFamily: 'DM Mono, monospace',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'white'
                }}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Legend table */}
          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {chartData.map((entry, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: COLORS[index % COLORS.length], flexShrink: 0 }} />
                <div style={{ flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'var(--text)' }}>{entry.name}</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)' }}>{((entry.value / total) * 100).toFixed(1)}%</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--text)', minWidth: '80px', textAlign: 'right' }}>{fmt(entry.value)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ExpenseBreakdown;